/**
 * Excel Adapter - Clean Architecture Excel Export Implementation
 *
 * This adapter handles Excel file generation using the xlsx library.
 * It follows clean architecture principles by isolating the Excel library dependency.
 */

import * as XLSX from 'xlsx';
import {ExportColumn, ExportConfig, ExportOptions} from '../../types/export.types';

/**
 * Excel Adapter Interface
 * Defines the contract for Excel file generation
 */
export interface ExcelAdapterInterface {
  generateExcelFile<T>(
    data: T[],
    config: ExportConfig<T>,
    options?: ExportOptions
  ): ArrayBuffer;
}

/**
 * Excel Adapter Implementation
 * Handles all Excel-specific logic using the xlsx library
 */
export class ExcelAdapter implements ExcelAdapterInterface {

  /**
   * Generate Excel file from data
   * Returns ArrayBuffer that can be used to create a Blob
   */
  generateExcelFile<T> (
    data: T[],
    config: ExportConfig<T>,
    options: ExportOptions = {}
  ): ArrayBuffer {

    // Prepare worksheet data
    const worksheetData = this.prepareWorksheetData(data, config, options);

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set numeric cell types for proper Excel handling
    this.setNumericCellTypes(worksheet, worksheetData);

    // Apply styling and formatting
    this.applyWorksheetFormatting(worksheet, worksheetData, config, options);

    // Create workbook
    const workbook = XLSX.utils.book_new();

    // Add worksheet to workbook
    const sheetName = this.sanitizeSheetName(config.title || config.filename);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Add metadata (author from options for multi-client branding)
    if (config.title) {
      workbook.Props = {
        Title: config.title,
        Subject: config.description || '',
        Author: options.author ?? 'Dashboard',
        CreatedDate: new Date()
      };
    }

    // Generate Excel file as ArrayBuffer
    const excelBuffer = XLSX.write(workbook, {
      type: 'array',
      bookType: 'xlsx',
      bookSST: false,
      compression: true
    });

    return excelBuffer;
  }

  /**
   * Prepare worksheet data from raw data
   * Returns 2D array with cell objects that include type information
   */
  private prepareWorksheetData<T> (
    data: T[],
    config: ExportConfig<T>,
    options: ExportOptions
  ): Array<Array<string | number | { t: string; v: number | string }>> {

    const {includeHeaders = true, includeTotals = false} = options;
    const visibleColumns = config.columns.filter(col => !col.hidden);

    const worksheetData: Array<Array<string | number | { t: string; v: number | string }>> = [];

    // Add headers if required
    if (includeHeaders) {
      const headers = visibleColumns.map(col => col.title);
      worksheetData.push(headers);
    }

    // Track numeric columns and their totals
    const numericColumnIndices: number[] = [];
    const columnTotals: number[] = new Array(visibleColumns.length).fill(0);

    // Add data rows
    for (const row of data) {
      const rowData = visibleColumns.map((col, colIndex) => {
        const value = row[col.key];
        const formattedValue = col.formatter
          ? col.formatter(value, row)
          : this.formatValue(value);

        // Check if this is a numeric value
        const numericValue = this.parseNumericValue(formattedValue);
        if (numericValue !== null) {
          // Track this column as numeric
          if (!numericColumnIndices.includes(colIndex)) {
            numericColumnIndices.push(colIndex);
          }
          // Add to total
          columnTotals[colIndex] += numericValue;
          // Return as number for proper Excel cell type
          return numericValue;
        }

        return formattedValue;
      });
      worksheetData.push(rowData);
    }

    // Add totals row if requested
    if (includeTotals && data.length > 0) {
      const totalsLabel = options.totalsLabel || 'الإجمالي';
      const totalsRow = visibleColumns.map((col, colIndex) => {
        if (numericColumnIndices.includes(colIndex)) {
          // Return total as number
          return columnTotals[colIndex];
        }
        // For first column, show totals label; otherwise empty
        return colIndex === 0 ? totalsLabel : '';
      });
      worksheetData.push(totalsRow);
    }

    return worksheetData;
  }

  /**
   * Parse string value to number if possible
   * Returns null if not a valid number
   */
  private parseNumericValue (value: string): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    // Remove any whitespace
    const trimmed = String(value).trim();

    // Try to parse as number
    const numValue = Number(trimmed);

    // Check if it's a valid number and not NaN
    if (!isNaN(numValue) && isFinite(numValue)) {
      return numValue;
    }

    return null;
  }

  /**
   * Format value for Excel cell
   */
  private formatValue (value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }

  /**
   * Set numeric cell types for proper Excel numeric handling
   * This ensures Excel treats numeric cells as numbers, not text
   */
  private setNumericCellTypes (
    worksheet: XLSX.WorkSheet,
    data: Array<Array<string | number | { t: string; v: number | string }>>
  ): void {
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');

    // Skip header row (row 0)
    for (let row = 1; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({r: row, c: col});
        const cell = worksheet[cellAddress];

        if (!cell) {continue;}

        // Get the value from the data array
        const rowData = data[row];
        if (!rowData || col >= rowData.length) {continue;}

        const cellValue = rowData[col];

        // If the value is already a number, set cell type to 'n' (number)
        if (typeof cellValue === 'number') {
          cell.t = 'n';
          cell.v = cellValue;
        } else if (typeof cellValue === 'string') {
          // Try to parse string as number
          const numericValue = this.parseNumericValue(cellValue);
          if (numericValue !== null) {
            cell.t = 'n';
            cell.v = numericValue;
          }
        }
      }
    }
  }

  /**
   * Apply formatting to worksheet
   * Sets column widths, header styling, and totals row formatting
   */
  private applyWorksheetFormatting<T> (
    worksheet: XLSX.WorkSheet,
    data: Array<Array<string | number | { t: string; v: number | string }>>,
    config: ExportConfig<T>,
    options: ExportOptions
  ): void {
    const visibleColumns = config.columns.filter(col => !col.hidden);

    // Set column widths based on content
    const columnWidths = visibleColumns.map((col, colIndex) => {
      const headerWeight = options.minimizeHeaders ? 0.8 : 1;
      let maxWidth = Math.ceil(col.title.length * headerWeight);

      const startRow = options.includeHeaders !== false ? 1 : 0;
      const endRow = options.includeTotals ? data.length : data.length - 1;

      for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
        const cellValue = data[rowIndex]?.[colIndex];
        if (cellValue !== undefined && cellValue !== null) {
          const cellValueStr = typeof cellValue === 'number'
            ? String(cellValue)
            : String(cellValue);
          maxWidth = Math.max(maxWidth, cellValueStr.length);
        }
      }

      const padding = options.minimizeHeaders ? 1 : 2;
      return {wch: Math.min(maxWidth + padding, 50)};
    });

    worksheet['!cols'] = columnWidths;

    // Set header row height
    if (options.includeHeaders !== false) {
      if (!worksheet['!rows']) {
        worksheet['!rows'] = [];
      }
      const headerHeight = options.minimizeHeaders ? 15 : 20;
      worksheet['!rows'][0] = {hpt: headerHeight};
    }

    // Apply formatting to header and totals rows
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    const totalsRowIndex = options.includeTotals ? range.e.r : -1;

    for (let col = range.s.c; col <= range.e.c; col++) {
      if (options.includeHeaders !== false) {
        const headerCellAddress = XLSX.utils.encode_cell({r: 0, c: col});
        if (worksheet[headerCellAddress] && !worksheet[headerCellAddress].s) {
          worksheet[headerCellAddress].s = {};
        }
      }

      if (totalsRowIndex >= 0) {
        const totalsCellAddress = XLSX.utils.encode_cell({r: totalsRowIndex, c: col});
        if (worksheet[totalsCellAddress] && !worksheet[totalsCellAddress].s) {
          worksheet[totalsCellAddress].s = {};
        }
      }
    }
  }

  /**
   * Sanitize sheet name for Excel compatibility
   * Excel sheet names have restrictions on length and characters
   */
  private sanitizeSheetName (name: string): string {
    // Remove invalid characters: \ / ? * [ ]
    let sanitized = name.replace(/[\\\/\?\*\[\]]/g, '');

    // Limit to 31 characters (Excel limit)
    sanitized = sanitized.substring(0, 31);

    // Ensure it's not empty
    if (!sanitized) {
      sanitized = 'Sheet1';
    }

    return sanitized;
  }
}

// Export singleton instance
export const excelAdapter = new ExcelAdapter();

